import { useMutation } from '@tanstack/react-query';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Spinner from 'react-bootstrap/Spinner';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { ApiError, AuthService, GoogleSocialLogin } from '../api';
import { ServerErrorsAlert } from '../app/components/ServerErrorsAlert';
import { SessionContext } from '../app/contexts/SessionContext';
import { useRequestStatus } from '../app/hooks/requests-status';
import { useDocumentTitle } from '../app/hooks/use-document-title';
import { handleApiError } from '../app/utils/errorhandling';

export const SocialLogin = () => {
  useDocumentTitle('Social network login');
  const navigate = useNavigate();
  const requestStatus = useRequestStatus();
  const [serverErrors, setServerErrors] = useState<ApiError>();
  const [showGlobalServerErrors, setShowGlobalServerErrors] = useState(false);
  const hasAttemptedRef = useRef(false);

  const { socialApp } = useParams();
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code');
  const state = searchParams.get('state');

  const { user } = useContext(SessionContext);

  // Stable mutation - won't change on re-renders
  const mutationGoogleSocialLogin = useMutation({
    mutationFn: async (data: GoogleSocialLogin) => {
      console.log('Starting mutation with data:', data);
      requestStatus.setInProgress();
      return AuthService.authSocialLoginGoogleCreate({
        requestBody: data,
      });
    },
    onSuccess: async (userData) => {
      console.log('Social login successful, user data:', userData);
      requestStatus.setCompleted();

      // Determine where to redirect based on user status and URL params
      const urlParams = new URLSearchParams(window.location.search);
      const nextUrl = urlParams.get('next') || localStorage.getItem('sessionContinueUrl') || null;
      // don't want this to be a sticky login redirect
      localStorage.removeItem('sessionContinueUrl');
      console.log('Next URL:', nextUrl);
      // Check if this is a new user who needs to complete registration
      const needsRegistrationCompletion = userData?.registration_status === 'MISSING_INFO';

      if (needsRegistrationCompletion) {
        // New user - go to registration completion
        window.location.href = '/register/complete';
      } else if (nextUrl) {
        // Existing user with a next parameter - redirect there
        window.location.href = decodeURIComponent(nextUrl);
      } else {
        window.location.href = '/app';
      }
    },

    onError: (errors: ApiError) => {
      requestStatus.setFailed();
      setServerErrors(errors);
      setShowGlobalServerErrors(true);
      // Reset ref on error to allow retry
      hasAttemptedRef.current = false;
      handleApiError(errors);
    },
  });

  // Stable callback that won't change unless code/state change
  const handleGoogleCallback = useCallback(async () => {
    if (
      socialApp === 'google' &&
      code &&
      state &&
      !requestStatus.isInProgress &&
      !hasAttemptedRef.current
    ) {
      hasAttemptedRef.current = true;

      try {
        await mutationGoogleSocialLogin.mutateAsync({ code, state });
      } catch (error) {
        // Error handling is done in mutation's onError
      }
    }
  }, [socialApp, code, state, requestStatus.isInProgress, mutationGoogleSocialLogin]);

  // Handle redirect if user is already logged in
  useEffect(() => {
    if (user) {
      console.log('User exists, navigating to /app');
      navigate('/app');
    }
  }, [navigate, user]);

  // Handle Google OAuth callback - only run once per code/state combo
  useEffect(() => {
    handleGoogleCallback();
  }, [handleGoogleCallback]);

  function handleClearAlert() {
    setShowGlobalServerErrors(false);
  }

  return (
    <Container className="h-100" fluid>
      <Row>
        <Col className="col-login" xs={12}>
          <Container
            className="content-wrapper justify-content-center align-items-center text-center"
            fluid
          >
            {requestStatus.isInProgress && (
              <Spinner animation="grow" className="loading-spinner" variant="primary" />
            )}
            <ServerErrorsAlert
              errors={serverErrors}
              showErrors={showGlobalServerErrors}
              clearErrors={handleClearAlert}
            />
          </Container>
        </Col>
      </Row>
    </Container>
  );
};
